import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as CapabilitiesIcon } from "./../assets/svgs/capabilities-icon.svg";
import { ReactComponent as CapabilitiesIconItem } from "./../assets/svgs/capabilities-icon-item.svg";
import { ReactComponent as LimitationsIcon } from "./../assets/svgs/limitations-icon.svg";
import { ReactComponent as LimitationsIconItem } from "./../assets/svgs/limitations-icon-item.svg";
import { ReactComponent as ExamplesIcon } from "./../assets/svgs/examples-icon.svg";
import { ReactComponent as ExamplesIconItem } from "./../assets/svgs/examples-icon-item.svg";
import { ReactComponent as MobilePopupIcon } from "./../assets/svgs/mobile-popup-icon.svg";
import { ReactComponent as DesktopSendIcon } from "./../assets/svgs/desktop-send-icon.svg";
import { ReactComponent as PersonalizedSearchImage } from "./../assets/svgs/personalized-search-icon.svg";
import MarkdownResponse from "./MarkdownResponse.js";
import {
  useMarketplaceConversationsListQuery,
  useLazyMarketplaceConversationMessagesQuery,
  useDeleteMarketplaceConversationMutation,
} from "../store/api.ts";
import toast from "react-hot-toast";
import { useMarketplaceChat } from "../context/MarketplaceChatProvider.js";

const MARKETPLACE_CONVERSATION_STORAGE_KEY = "marketplace_chat_conversation_id";

const normalizeConversationsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.conversations)) return data.conversations;
  return [];
};

const getConversationId = (conversation) => {
  const id = conversation?.id || conversation?.conversation_id;
  return id ? String(id) : "";
};

const capitalizeFirstCharacter = (value = "") => {
  const text = String(value).trim();

  if (!text) return "";

  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getConversationTitle = (conversation) => {
  const title =
    conversation?.title ||
    conversation?.description ||
    conversation?.name ||
    conversation?.last_message ||
    "Description";
  return capitalizeFirstCharacter(title);
};

const mapApiMessagesToUiMessages = (messages = []) => {
  return messages.map((message) => ({
    role: message.role === "user" ? "user" : "assistant",
    text: message.content || "",
    imageUrl: message.image_url || null,
    products: Array.isArray(message.products) ? message.products : [],
  }));
};

const MobileModeSelect = ({ mode, onModeChange }) => {
  return (
    <div className="relative shrink-0 md:hidden">
      <select
        value={mode}
        onChange={(event) => onModeChange?.(event.target.value)}
        aria-label="Choose chat mode"
        className="
          h-[27px] min-w-[64px] appearance-none
          rounded-[5px] border-0 bg-[#A64346]
          pb-[1px] pl-[12px] pr-[24px]
          font-spaceMono text-[10px] font-semibold uppercase
          tracking-[0.12em] text-white
          outline-none transition-opacity hover:opacity-95
          focus:ring-2 focus:ring-white/20
        "
      >
        <option value="find">Find</option>
        <option value="style">Style</option>
      </select>

      <span
        aria-hidden="true"
        className="
          pointer-events-none absolute right-[15px] top-1/2
          -translate-y-1/2 text-[11px] text-white
        "
      >
        ▾
      </span>
    </div>
  );
};

const DownArrowIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="m6 10 6 6 6-6" />
  </svg>
);

const ModeButton = ({ active, children, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        `
          hidden h-[27px] min-w-[64px] shrink-0
          items-center justify-center rounded-[5px]
          px-[12px] pt-[2px] 
          font-spaceMono text-[10px] uppercase
          tracking-[0.12em] text-white
          transition-colors md:inline-flex
        `,
        active
          ? "bg-[#8F333B] hover:bg-[#9B303B] font-bold"
          : "bg-[#FFFFFF1A] hover:bg-[#414141] font-normal",
      ].join(" ")}
    >
      <span>{children}</span>

      <span
        aria-hidden="true"
        className="ml-[7px] text-[8px] leading-none text-white/90"
      >
        ▾
      </span>
    </button>
  );
};

const ModalCloseButton = ({ onClick }) => {
  return (
    <button
      type="button"
      aria-label="Close modal"
      onClick={onClick}
      className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-[#F4F4F4] text-[18px] leading-none text-[#1A1A1A] transition-colors hover:bg-[#E8E8E8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/20"
    >
      ×
    </button>
  );
};

const ChatProductCard = ({ product }) => {
  const score =
    product?.product_score !== null && product?.product_score !== undefined
      ? Math.round(Number(product.product_score))
      : null;

  const hasProductExplanation =
    Boolean(product?.why_this_works) || Boolean(product?.what_to_avoid);

  return (
    <div className="mt-3 w-full max-w-[360px]">
      <div className="rounded-[5px] border border-[#DFDFDF] bg-white p-3">
        <div className="flex gap-3">
          <div className="h-[94px] w-[68px] shrink-0 overflow-hidden rounded-[4px] bg-[#D9D9D9]">
            {product?.image_url ? (
              <img
                src={product.image_url}
                alt={product?.name || "Product"}
                className="h-full w-full object-cover"
              />
            ) : null}
          </div>

          <div className="min-w-0 flex flex-col justify-center flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="m-0 truncate text-[14px] font-medium text-black">
                  {product?.brand || "Brand"}
                </p>

                <p className="mt-1 line-clamp-1 text-[11px] text-[#1A1A26] font-normal">
                  {product?.name || "Product"}
                </p>
              </div>

              {product?.price ? (
                <p className="m-0 shrink-0 text-[14px] font-medium text-black">
                  ${Number(product.price).toFixed(0)}
                </p>
              ) : null}
            </div>

            <div className="mt-2 flex items-center justify-between gap-2">
              {score !== null ? (
                <span className="h-[26px] rounded-[3px] bg-[#DBE7FA] px-2 py-1 font-normal text-[12px] text-[#3D648B] font-spaceMono">
                  {score}% match on you
                </span>
              ) : null}
              {product?.slug ? (
                <a
                  href={product.slug}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] font-normal text-[#595973] hover:underline"
                >
                  View Product
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {hasProductExplanation ? (
        <div className="mt-3 space-y-3">
          {product?.why_this_works ? (
            <div className="rounded-[5px] bg-[#EEF8F2] px-4 py-3 text-left">
              <div className="flex items-center gap-3 text-[12px] font-medium text-[#3D8B5E]">
                <span>✓</span>
                <span>Why this works</span>
              </div>

              <p className="mt-[6px] text-[12px] leading-[1.35] text-[#335940] font-normal">
                {product.why_this_works}
              </p>
            </div>
          ) : null}

          {product?.what_to_avoid ? (
            <div className="rounded-[5px] bg-[#FFF0F0] px-4 py-3 text-left">
              <div className="flex items-center gap-3 text-[12px] font-medium text-[#B85C5C]">
                <span>×</span>
                <span>What to avoid</span>
              </div>

              <p className="mt-[6px] text-[12px] leading-[1.35] text-[#8C4747]">
                {product.what_to_avoid}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ChatHistoryPanel = ({
  conversations = [],
  isLoading,
  error,
  activeConversationId,
  onSelectConversation,
}) => {
  if (isLoading) {
    return (
      <div className="w-full rounded-[2px] bg-[#EFEFEF] px-4 py-6 text-center text-[12px] text-[#777777]">
        <span className="mr-2 inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#D5D7DE] border-t-[#777777]" />
        Loading history...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full rounded-[2px] bg-[#EFEFEF] px-4 py-6 text-center text-[12px] text-[#B54747]">
        Failed to load chat history.
      </div>
    );
  }

  if (!conversations.length) {
    return (
      <div className="w-full rounded-[2px] bg-[#EFEFEF] px-4 py-6 text-center text-[12px] text-[#777777]">
        No chats found.
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-[2px] bg-[#EFEFEF] py-1">
      <div className="max-h-[320px] overflow-y-auto pr-1">
        {conversations.map((conversation, index) => {
          const conversationId = getConversationId(conversation);
          const isActive =
            activeConversationId &&
            String(activeConversationId) === conversationId;

          return (
            <button
              key={conversationId || index}
              type="button"
              onClick={() => {
                if (conversationId) onSelectConversation?.(conversationId);
              }}
              className={[
                "flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-white/40",
                isActive ? "bg-white/80" : "",
              ].join(" ")}
            >
              <span className="min-w-0 truncate text-[14px] font-normal text-[#0F0F0F]">
                {getConversationTitle(conversation)}
              </span>

              <span className="shrink-0 text-[18px] leading-none text-[#8A8580] font-medium">
                +
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DeleteChatConfirmModal = ({ isDeleting, onCancel, onConfirm }) => {
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/45 px-4">
      <div className="w-full max-w-[360px] rounded-[8px] bg-white p-5 shadow-[0_18px_50px_rgba(0,0,0,0.28)]">
        <p className="m-0 text-[17px] font-semibold text-[#111111]">
          Delete this chat?
        </p>

        <p className="mt-2 text-[13px] leading-[1.45] text-[#666666]">
          This conversation will be deleted permanently. This action cannot be
          undone.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="h-[34px] rounded-[5px] bg-[#F2F2F2] px-4 text-[12px] font-medium text-[#333333] transition hover:bg-[#E8E8E8] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="h-[34px] rounded-[5px] bg-[#C95353] px-4 text-[12px] font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyChatState = () => {
  const columns = [
    {
      icon: <ExamplesIcon />,
      title: "Examples",
      items: [
        {
          icon: <ExamplesIconItem />,
          text: "Does this dress suits me for a date?",
        },
        {
          icon: <ExamplesIconItem />,
          text: "What do I wear with this little black dress?",
        },
      ],
    },
    {
      icon: <CapabilitiesIcon />,
      title: "Capabilities",
      items: [
        {
          icon: <CapabilitiesIconItem />,
          text: "Understands Your Unique Style",
        },
        {
          icon: <CapabilitiesIconItem />,
          text: "Ensures Smart and Secure Shopping",
        },
      ],
    },
    {
      icon: <LimitationsIcon />,
      title: "Limitations",
      items: [
        {
          icon: <LimitationsIconItem />,
          text: "AI Cannot Replace Personal Taste",
        },
        {
          icon: <LimitationsIconItem />,
          text: "Limited Context Awareness",
        },
      ],
    },
  ];

  return (
    <div className="flex min-h-full w-full items-center justify-center px-4 py-8">
      <div className="grid w-full max-w-[615px] grid-cols-1 gap-y-7 md:grid-cols-3 md:gap-x-4">
        {columns.map((column) => (
          <div key={column.title} className="text-center">
            <div className="mb-[26px] flex h-[58px] flex-col items-center justify-start gap-2">
              <div className="flex h-[24px] items-center justify-center">
                {column.icon}
              </div>

              <p className="m-0 h-[22px] text-center font-inter text-[15px] font-normal leading-[22px] text-black">
                {column.title}
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              {column.items.map((item, index) => (
                <div
                  key={`${column.title}-${index}`}
                  className="flex h-[82px] w-full max-w-[185px] flex-col justify-start rounded-[5px] border border-[#AEAEAE] bg-white px-3 py-[10px] text-left"
                >
                  <div className="mb-2 flex h-[14px] items-center">
                    {item.icon}
                  </div>

                  <p className="m-0 font-spaceMono text-[10px] leading-[1.45] text-[#22272A]">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChatMessagesLoadingOverlay = () => {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/55 backdrop-blur-[2px]">
      <div className="flex flex-col items-center rounded-[10px] bg-[#111111]/90 px-8 py-7 shadow-[0_20px_70px_rgba(0,0,0,0.45)]">
        <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-white/25 border-t-white" />

        <p className="mt-4 font-montserrat text-[14px] font-medium text-white">
          Loading messages...
        </p>

        <p className="mt-1 font-montserrat text-[11px] text-white/55">
          Please wait while we open this chat.
        </p>
      </div>
    </div>
  );
};

const UserMessageImage = ({ imageFile, imageUrl }) => {
  const [resolvedImageUrl, setResolvedImageUrl] = useState("");

  useEffect(() => {
    if (imageUrl) {
      setResolvedImageUrl(imageUrl);
      return undefined;
    }

    if (!(imageFile instanceof Blob)) {
      setResolvedImageUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setResolvedImageUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile, imageUrl]);

  if (!resolvedImageUrl) return null;

  return (
    <img
      src={resolvedImageUrl}
      alt="User uploaded"
      className="max-h-[280px] w-full object-cover"
    />
  );
};

const AssistantChatModal = ({
  messages = [],
  streamData,
  isStreaming,
  isAuthenticated = false,
  currentConversationId,
  onConversationChange,
  onMessagesLoaded,
  onNewChat,
  onDeleteCurrentChat,
  onClose,
}) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [localConversations, setLocalConversations] = useState([]);

  const [deleteMarketplaceConversation, { isLoading: isDeletingConversation }] =
    useDeleteMarketplaceConversationMutation();
  const scrollContainerRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const previousMessageCountRef = useRef(messages.length);
  const programmaticScrollRef = useRef(false);

  const isNearBottom = (element) => {
    if (!element) return true;

    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;

    return distanceFromBottom < 120;
  };

  const scrollToBottom = (behavior = "auto") => {
    const element = scrollContainerRef.current;
    if (!element) return;

    programmaticScrollRef.current = true;

    requestAnimationFrame(() => {
      const nextTop = element.scrollHeight;

      if (behavior === "smooth") {
        element.scrollTo({
          top: nextTop,
          behavior: "smooth",
        });
      } else {
        element.scrollTop = nextTop;
      }

      window.setTimeout(
        () => {
          programmaticScrollRef.current = false;
          shouldStickToBottomRef.current = isNearBottom(element);
        },
        behavior === "smooth" ? 180 : 0,
      );
    });
  };

  const handleChatScroll = () => {
    const element = scrollContainerRef.current;
    if (!element) return;

    if (programmaticScrollRef.current) return;

    shouldStickToBottomRef.current = isNearBottom(element);
  };
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
    error: historyError,
  } = useMarketplaceConversationsListQuery(undefined, {
    skip: !isAuthenticated || !isHistoryOpen,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [getConversationMessages, { isLoading: isThreadLoading }] =
    useLazyMarketplaceConversationMessagesQuery();
  const apiConversations = useMemo(
    () => normalizeConversationsResponse(historyData),
    [historyData],
  );

  const conversations = useMemo(() => {
    const seenIds = new Set();
    const merged = [];

    [...localConversations, ...apiConversations].forEach((conversation) => {
      const conversationId = getConversationId(conversation);

      if (!conversationId || seenIds.has(conversationId)) return;

      seenIds.add(conversationId);
      merged.push(conversation);
    });

    return merged;
  }, [localConversations, apiConversations]);
  const handleOpenHistory = () => {
    if (!isAuthenticated) return;
    setIsHistoryOpen((prev) => !prev);
  };

  const handleSelectConversation = async (conversationId) => {
    if (!conversationId) return;
    const nextConversationId = String(conversationId);
    const previousConversationId = currentConversationId ? String(currentConversationId) : null;

    try {
      setIsMessagesLoading(true);
      setIsHistoryOpen(false);
      localStorage.setItem(MARKETPLACE_CONVERSATION_STORAGE_KEY, nextConversationId);
      onConversationChange?.(nextConversationId);

      const response = await getConversationMessages(
        {
          conversation_id: nextConversationId,
        },
        true,
      ).unwrap();

      const mappedMessages = mapApiMessagesToUiMessages(
        response?.messages || [],
      );

      onMessagesLoaded?.(mappedMessages);

      setTimeout(() => {
        shouldStickToBottomRef.current = true;
        scrollToBottom("auto");
      }, 0);
    } catch (error) {
      if (previousConversationId) localStorage.setItem(MARKETPLACE_CONVERSATION_STORAGE_KEY, previousConversationId);
      else  localStorage.removeItem(MARKETPLACE_CONVERSATION_STORAGE_KEY);
      onConversationChange?.(previousConversationId);
      console.error("Failed to load marketplace conversation:", error);
      toast.error("Failed to load chat messages.");
    } finally {
      setIsMessagesLoading(false);
    }
  };

  const handleNewChatClick = () => {
    localStorage.removeItem(MARKETPLACE_CONVERSATION_STORAGE_KEY);
    onConversationChange?.(null);
    onMessagesLoaded?.([]);
    onNewChat?.();
    setIsHistoryOpen(false);
  };
  const handleDeleteChatClick = () => {
    if (!currentConversationId) {
      toast.error("No saved conversation selected to delete.");
      return;
    }

    setIsDeleteConfirmOpen(true);
  };

  const handleCancelDeleteChat = () => {
    if (isDeletingConversation) return;
    setIsDeleteConfirmOpen(false);
  };

  const handleConfirmDeleteChat = async () => {
    if (!currentConversationId) {
      toast.error("No saved conversation selected to delete.");
      setIsDeleteConfirmOpen(false);
      return;
    }

    try {
      const response = await deleteMarketplaceConversation({
        conversation_id: String(currentConversationId),
      }).unwrap();

      localStorage.removeItem(MARKETPLACE_CONVERSATION_STORAGE_KEY);

      onConversationChange?.(null);
      onMessagesLoaded?.([]);
      onDeleteCurrentChat?.(currentConversationId);
      setLocalConversations((prev) =>
        prev.filter(
          (conversation) =>
            getConversationId(conversation) !== String(currentConversationId),
        ),
      );

      setIsHistoryOpen(false);
      setIsDeleteConfirmOpen(false);

      toast.success(response?.message || "Conversation deleted successfully.");
    } catch (error) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.error ||
        "Failed to delete conversation.";

      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const showLiveAssistant =
    Boolean(streamData) &&
    (isStreaming || !streamData.done) &&
    !streamData.error;

  useEffect(() => {
    const streamedConversationId =
      streamData?.conversationId || streamData?.conversation_id;

    if (!streamedConversationId) return;

    const conversationId = String(streamedConversationId);

    const firstUserMessage = messages.find(
      (message) => message?.role === "user" && message?.text?.trim(),
    );

    const title = firstUserMessage?.text?.trim();

    if (!title) return;

    setLocalConversations((prev) => {
      const alreadyExistsLocally = prev.some(
        (conversation) => getConversationId(conversation) === conversationId,
      );

      if (alreadyExistsLocally) return prev;

      const alreadyExistsInApi = apiConversations.some(
        (conversation) => getConversationId(conversation) === conversationId,
      );

      if (alreadyExistsInApi) return prev;

      return [
        {
          id: conversationId,
          conversation_id: conversationId,
          title,
          description: title,
          last_message: title,
          source: "marketplace",
          isLocalOnly: true,
        },
        ...prev,
      ];
    });
  }, [
    streamData?.conversationId,
    streamData?.conversation_id,
    messages,
    apiConversations,
  ]);

  useLayoutEffect(() => {
    const previousCount = previousMessageCountRef.current;
    const currentCount = messages.length;
    const hasNewMessage = currentCount > previousCount;
    const lastMessage = messages[currentCount - 1];

    previousMessageCountRef.current = currentCount;

    if (!hasNewMessage) return;

    if (lastMessage?.role === "user") {
      shouldStickToBottomRef.current = true;
      scrollToBottom("smooth");
      return;
    }

    if (shouldStickToBottomRef.current) {
      scrollToBottom("auto");
    }
  }, [messages.length]);

  useLayoutEffect(() => {
    if (!showLiveAssistant) return;
    if (!shouldStickToBottomRef.current) return;

    scrollToBottom("auto");
  }, [
    showLiveAssistant,
    streamData?.text,
    streamData?.statusMessage,
    streamData?.products?.length,
  ]);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/78 px-3 pb-4 pt-8 md:pb-5">
      <button
        type="button"
        aria-label="Close chat modal"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <div className="relative z-10 flex h-[calc(100vh-56px)] w-full max-w-[1002px] flex-col overflow-hidden rounded-[7px] bg-white pb-[104px] shadow-[0_18px_60px_rgba(0,0,0,0.35)] md:h-[calc(100vh-32px)]">
        <ModalCloseButton onClick={onClose} />
        {isMessagesLoading ? <ChatMessagesLoadingOverlay /> : null}
        {isDeleteConfirmOpen ? (
          <DeleteChatConfirmModal
            isDeleting={isDeletingConversation}
            onCancel={handleCancelDeleteChat}
            onConfirm={handleConfirmDeleteChat}
          />
        ) : null}
        {isAuthenticated ? (
          <div className="absolute left-3 top-3 z-20 flex overflow-hidden rounded-[5px] bg-[#3B3A3A] p-[5px] gap-[10px]">
            <button
              type="button"
              onClick={handleOpenHistory}
              className={[
                "rounded-[5px] px-[10px] py-[5px] text-[13px] transition-colors gap-[10px] font-montserrat text-[#818181]",
                isHistoryOpen
                  ? "bg-white font-medium"
                  : "hover:text-white font-normal",
              ].join(" ")}
            >
              History
            </button>
            <button
              type="button"
              onClick={handleNewChatClick}
              className="rounded-[5px] px-[10px] py-[5px] text-[13px] transition-colors gap-[10px] font-montserrat text-[#818181] hover:text-white font-normal"
            >
              New Chat
            </button>
            <button
              type="button"
              onClick={handleDeleteChatClick}
              disabled={isDeletingConversation || !currentConversationId}
              className="rounded-[5px] px-[10px] py-[5px] text-[13px] font-normal font-montserrat text-[#818181] transition-colors gap-[10px] enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isDeletingConversation ? "Deleting..." : "Delete Chat"}
            </button>{" "}
          </div>
        ) : null}
        {isHistoryOpen ? (
          <>
            <button
              type="button"
              aria-label="Close history"
              onClick={() => setIsHistoryOpen(false)}
              className="absolute inset-0 z-[18] cursor-default bg-transparent"
            />

            <div className="absolute left-3 top-[57px] z-20 w-[216px] max-w-[calc(100%-24px)] md:w-[218px]">
              <ChatHistoryPanel
                conversations={conversations}
                isLoading={
                  isHistoryLoading || (!historyData && isHistoryFetching)
                }
                error={historyError}
                activeConversationId={currentConversationId}
                onSelectConversation={handleSelectConversation}
              />
            </div>
          </>
        ) : null}{" "}
        <div
          ref={scrollContainerRef}
          onScroll={handleChatScroll}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-6 pt-16 md:px-14 md:py-8 md:pt-16"
        >
          <>
            {messages.length === 0 && !showLiveAssistant ? (
              <EmptyChatState />
            ) : null}

            {messages.length > 0 || showLiveAssistant ? (
              <div className="space-y-5">
                {messages.map((message, index) => {
                  if (message.role === "user") {
                    const savedImageUrl = message.imageUrl || "";
                    return (
                      <div
                        key={`${message.role}-${index}`}
                        className="flex justify-end"
                      >
                        <div className="max-w-[315px] overflow-hidden rounded-[5px] bg-[#F4F4F4]">
                          <UserMessageImage
                            imageFile={message.imageFile}
                            imageUrl={savedImageUrl}
                          />
                          {message.text ? (
                            <div className="px-5 py-3 text-[14px] font-normal leading-[1.35] text-[#0D0D0D]">
                              {message.text}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`${message.role}-${index}`}
                      className="flex gap-4"
                    >
                      <div className="mt-1 h-[31px] w-[31px] shrink-0 rounded-[5px] bg-[#252525]" />

                      <div className="max-w-[620px]">
                        <div
                          className={[
                            "chat-markdown text-[14px] leading-[1.55] font-normal",
                            message.isError
                              ? "text-[#B54747]"
                              : "text-[#0D0D0D]",
                          ].join(" ")}
                        >
                          <MarkdownResponse message={message.text || ""} />
                        </div>

                        {Array.isArray(message.products) &&
                          message.products.map((product) => (
                            <ChatProductCard
                              key={product.id}
                              product={product}
                            />
                          ))}

                        {/* <div className="mt-3 flex items-center gap-2 text-[15px] text-[#555555]">
                          <button type="button" aria-label="Copy response">
                            ⧉
                          </button>
                          <button type="button" aria-label="Retry response">
                            ↻
                          </button>
                        </div> */}
                      </div>
                    </div>
                  );
                })}

                {showLiveAssistant ? (
                  <div className="flex gap-4">
                    <div className="mt-1 h-[31px] w-[31px] shrink-0 rounded-[5px] bg-[#252525]" />

                    <div className="max-w-[620px]">
                      {streamData?.text ? (
                        <div className="chat-markdown text-[14px] leading-[1.55] text-[#1A1A1A]">
                          <MarkdownResponse message={streamData.text || ""} />
                        </div>
                      ) : (
                        <div className="flex min-h-[31px] items-center gap-2 text-[13px] text-[#777777]">
                          <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#D5D7DE] border-t-[#777777]" />
                          <span>
                            {streamData?.statusMessage || "Thinking..."}
                          </span>
                        </div>
                      )}
                      {Array.isArray(streamData?.products) &&
                        streamData.products.map((product) => (
                          <ChatProductCard key={product.id} product={product} />
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        </div>{" "}
      </div>
    </div>
  );
};

const PersonalizedSearchModal = ({
  mode,
  value,
  onModeChange,
  onChange,
  onSubmit,
  onClose,
}) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const isFindMode = mode === "find";

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/78 px-3 pb-4 pt-8 md:pb-5">
      <button
        type="button"
        aria-label="Close chat modal"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <div className="relative z-10 flex h-[calc(100vh-56px)] w-full max-w-[1002px] flex-col items-center overflow-hidden rounded-[5px] bg-white pb-[96px] shadow-[0_18px_60px_rgba(0,0,0,0.35)] md:h-[calc(100vh-32px)] md:pb-[104px] pt-3 md:pt-5">
        <ModalCloseButton onClick={onClose} />

        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-5 pt-8">
          <div className="mb-3 text-[36px] leading-none text-[#0F0F0F] font-semibold">
            ✦
          </div>
          <div className="m-0 text-center font-helvena text-[18px] font-medium leading-tight text-[#0F0F0F]">
            Unlock Personalized Search
          </div>

          <p className="mt-3 max-w-[300px] text-center font-helvena text-[14px] leading-[1.35] text-[#8A8580] font-normal">
            Complete your profile to get AI-powered product recommendations
            tailored to your body, fit, and style preferences.
          </p>
          {isFindMode ? (
            <div className="relative mt-4 flex w-full justify-center">
              <PersonalizedSearchImage />
            </div>
          ) : (
            <div className="mt-5 w-full max-w-[450px] space-y-3">
              <div className="rounded-[5px] bg-[#EEF8F2] px-[14px] pt-[14px] pb-[12px] text-left">
                <div className="flex items-center gap-[10px] text-[12px] font-medium text-[#3D8B5E]">
                  <span className="font-normal">✓</span>
                  <span>Why this works</span>
                </div>
                <p className="mt-[6px] text-[12px] leading-[1.35] text-[#335940] font-medium">
                  This style complements your body proportions and matches your
                  preference for structured silhouettes with waist definition.
                </p>
              </div>

              <div className="rounded-[5px] bg-[#FFF0F0] px-[14px] pt-[14px] pb-[12px] text-left">
                <div className="flex items-center gap-[10px] text-[12px] font-medium text-[#B85C5C]">
                  <span className="font-normal">✗</span>
                  <span>What to avoid</span>
                </div>
                <p className="mt-[6px] font-normal text-[12px] leading-[1.35] text-[#8C4747]">
                  Oversized fits may overwhelm your frame. Look for tailored or
                  slim cuts instead.
                </p>
              </div>
              <div className="rounded-[5px] bg-[#F0F4FC] px-[14px] pt-[14px] pb-[12px] text-left">
                <div className="flex items-center gap-[10px] text-[12px] font-medium text-[#5B7AB5]">
                  <span className="font-normal">→</span>
                  <span>Recommendations</span>
                </div>
                <ul className="mt-[6px] space-y-1 text-[12px] leading-[1.3] text-[#40598C] font-normal">
                  <li>· Try empire waist styles</li>
                  <li>· Look for V-neck designs</li>
                  <li>· Consider midi lengths</li>
                  <li>· A-line silhouettes work well</li>
                </ul>
              </div>
            </div>
          )}

          <div className={isFindMode ? "mt-3" : "mt-3"}>
            <div className="h-full md:h-[33px] flex items-center gap-3 rounded-[5px] bg-[#FFFFFF] font-normal px-4 md:px-7 py-1 md:py-4 text-[12px] text-[#8A8580] shadow-[0px_0px_43.4px_0px_#00000040]">
              <span className="text-[#8A8580]">⏱</span>
              <span className="font-mono tracking-[0.08em]">
                Setting up profile takes about 2 minutes
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FindRecommendationsPreview = ({
  query,
  products = [],
  isOpen,
  onViewProducts,
  onViewGpt,
}) => {
  if (!isOpen) return null;

  const previewProducts = products.slice(0, 5);

  return (
    <div className="mb-2 overflow-hidden bg-white px-2 py-1">
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 items-center gap-1">
          {previewProducts.map((product, index) => (
            <div
              key={product?.id || `${product?.name}-${index}`}
              className="h-[96px] w-[86px] shrink-0 overflow-hidden rounded-[4px] bg-[#CFCFCF]"
            >
              {product?.image_url || product?.image ? (
                <img
                  src={product.image_url || product.image}
                  alt={product?.name || product?.product_name || "Product"}
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>
          ))}
        </div>

        <div className="flex min-w-0 flex-col items-center md:items-start">
          <p className="m-0 line-clamp-1 text-center text-[14px] font-normal text-[#1E1E1E] md:text-left">
            here are your {products.length || previewProducts.length}{" "}
            personalised
            {query ? ` “${query}”` : ""} recommendations
          </p>

          <div className="mt-2 grid w-full grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onViewProducts}
              className="h-[42px] rounded-[5px] bg-[#313131] px-5 pb-[1px] text-[14px] font-normal text-white transition hover:opacity-90"
            >
              View products
            </button>

            <button
              type="button"
              onClick={onViewGpt}
              className="h-[42px] rounded-[5px] bg-[#C4C4C4] px-5 pb-[1px] text-[14px] font-normal text-[#3C3C3C] transition hover:bg-[#BDBDBD]"
            >
              View in GPT instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FloatingChatBar = ({
  onStartSurvey,
  onRequireProfile,
  canUseRealChat = false,
  shouldShowSurveyPrompt = true,
  isProfileChecking = false,
  isAuthenticated = false,
}) => {
  const {
    chatMode: mode,
    setChatMode,
    chatInput: value,
    setChatInput,
    chatImage: selectedImage,
    setChatImage,
    isChatModalOpen: isModalOpen,
    setIsChatModalOpen,
    marketplaceMessages: messages,
    activeChatStreamData: streamData,
    isStreaming,
    isFindPreviewLoading: isFindSearching,
    findPreviewOpen,
    setFindPreviewOpen,
    findPreviewQuery,
    findPreviewProducts,
    marketplaceConversationId: currentConversationId,
    submitMessage,
    toggleChatModal,
    closeChatModal,
    startNewChat,
    handleDeletedConversation,
    handleMarketplaceConversationChange,
    handleMessagesLoaded,
    viewFindProducts,
    viewFindGpt,
  } = useMarketplaceChat();

  const onOpenPopup = () => {
    if (isProfileChecking) {
      toast.loading("Checking your profile...");
      return;
    }

    toggleChatModal();
  };

  const onImageChange = setChatImage;
  const onImageRemove = () => setChatImage(null);
  const onViewFindProducts = viewFindProducts;
  const onViewFindGpt = viewFindGpt;
  const onConversationChange = handleMarketplaceConversationChange;
  const onMessagesLoaded = handleMessagesLoaded;
  const onNewChat = startNewChat;
  const onDeleteCurrentChat = handleDeletedConversation;
  const onModeChange = setChatMode;
  const onCloseModal = closeChatModal;

  const onChange = (nextValue) => {
    setChatInput(nextValue);

    if (findPreviewOpen) {
      setFindPreviewOpen(false);
    }

    if (!canUseRealChat && nextValue.trim().length >= 1) {
      setIsChatModalOpen(true);
      return;
    }

    if (!canUseRealChat && nextValue.trim().length === 0) {
      setIsChatModalOpen(false);
    }
  };

  const onSubmit = () =>
    submitMessage({
      canUseRealChat,
      isProfileChecking,
      onRequireProfile: onRequireProfile || onStartSurvey,
    });

  const isFindActive = mode === "find";
  const isStyleActive = mode === "style";

  const fileInputRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  useEffect(() => {
    if (!selectedImage) {
      setImagePreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedImage);
    setImagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedImage]);

  const handleImageSelect = (event) => {
    const input = event.target;
    const file = input.files?.[0];

    input.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }
    onImageChange?.(file);
  };

  const handleRemoveImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onImageRemove?.();
  };

  const handleInputChange = (event) => {
    onChange?.(event.target.value);
  };

  const isSurveyPromptMode = !canUseRealChat;

  const isAssistantResponsePending =
    Boolean(streamData) && !streamData.done && !streamData.error;

  const hasInputValue = Boolean(value.trim());

  const shouldShowStartSurveyButton =
    !isAuthenticated &&
    !hasInputValue &&
    typeof onStartSurvey === "function";

  const isBusy =
    isFindSearching || isStreaming || isAssistantResponsePending;

  const isSubmitDisabled = isBusy || !hasInputValue;

  const primaryButtonLabel = isBusy
    ? "Please wait"
    : shouldShowStartSurveyButton
      ? "Start Survey"
    : hasInputValue
      ? isSurveyPromptMode
        ? "Start survey"
        : "Send message"
      : isModalOpen
        ? "Close chat"
        : "Open chat";

  const handlePrimaryButtonClick = () => {
    if (isBusy) return;
    if (shouldShowStartSurveyButton) {
      onStartSurvey?.();
      return;
    }
    if (!hasInputValue) {
      onOpenPopup?.();
      return;
    }
    handleSubmit();
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    onSubmit?.();
  };

  return (
    <>
      <style>{`
      .chat-markdown p {
        margin: 0 0 10px;
      }

      .chat-markdown p:last-child {
        margin-bottom: 0;
      }

      .chat-markdown h1,
      .chat-markdown h2,
      .chat-markdown h3 {
        margin: 14px 0 8px;
        font-weight: 700;
        line-height: 1.25;
        color: #111111;
      }

      .chat-markdown h1 {
        font-size: 22px;
      }

      .chat-markdown h2 {
        font-size: 18px;
      }

      .chat-markdown h3 {
        font-size: 16px;
      }

      .chat-markdown ul,
      .chat-markdown ol {
        margin: 8px 0 12px 20px;
        padding-left: 16px;
      }

      .chat-markdown ul {
        list-style-type: disc;
      }

      .chat-markdown ol {
        list-style-type: decimal;
      }

      .chat-markdown li {
        margin: 4px 0;
      }

      .chat-markdown strong {
        font-weight: 700;
      }

      .chat-markdown em {
        font-style: italic;
      }
    `}</style>

      {isModalOpen && canUseRealChat && !isProfileChecking ? (
        <AssistantChatModal
          messages={messages}
          streamData={streamData}
          isStreaming={isStreaming}
          isAuthenticated={isAuthenticated}
          currentConversationId={currentConversationId}
          onConversationChange={onConversationChange}
          onMessagesLoaded={onMessagesLoaded}
          onNewChat={onNewChat}
          onDeleteCurrentChat={onDeleteCurrentChat}
          onClose={onCloseModal}
        />
      ) : null}
      {isModalOpen &&
      (!canUseRealChat || shouldShowSurveyPrompt || isProfileChecking) ? (
        <PersonalizedSearchModal
          mode={mode}
          value={value}
          onModeChange={onModeChange}
          onChange={onChange}
          onSubmit={handleSubmit}
          onClose={onCloseModal}
        />
      ) : null}

      <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-[120] px-2 md:bottom-5 md:px-3">
        <div className="mx-auto w-full max-w-[984px]">
          <div
            className={[
              `
          pointer-events-auto mx-3 mb-3 overflow-hidden
          shadow-[0_12px_34px_rgba(0,0,0,0.26)]
          md:mx-0 md:mb-0
        `,
              findPreviewOpen ? "rounded-[8px] bg-white p-2" : "rounded-[8px]",
            ].join(" ")}
          >
            <FindRecommendationsPreview
              query={findPreviewQuery}
              products={findPreviewProducts}
              isOpen={findPreviewOpen}
              onViewProducts={onViewFindProducts}
              onViewGpt={onViewFindGpt}
            />

            <div
              className="
          flex min-h-[50px] items-center
          rounded-[5px] bg-[#1E1E1E]
          px-[10px] py-[8px]
           gap-[10px] shadow-[0px_10px_40px_0px_#00000033]
        "
            >
              {/* Mobile mode selector */}
              {isAuthenticated ? (
                <MobileModeSelect mode={mode} onModeChange={onModeChange} />
              ) : null}

              {/* Desktop mode buttons */}
              <ModeButton
                active={isFindActive}
                onClick={() => onModeChange?.("find")}
              >
                Find
              </ModeButton>

              <ModeButton
                active={isStyleActive}
                onClick={() => onModeChange?.("style")}
              >
                Style
              </ModeButton>

              {/* Image upload button */}
               <div className="relative shrink-0">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label={
                    selectedImage ? "Replace uploaded image" : "Upload an image"
                  }
                  title={selectedImage ? "Replace image" : "Upload image"}
                  className={[
                    `
                      inline-flex h-[27px] w-[28px]
                      items-center justify-center
                      rounded-[5px] bg-transparent
                      font-spaceMono text-[22px] font-normal
                      leading-none text-white
                      transition-opacity
                      md:bg-[#343434] md:text-[17px]
                    `,
                    isBusy
                      ? "cursor-not-allowed opacity-45"
                      : "cursor-pointer hover:opacity-80",
                  ].join(" ")}
                >
                  +
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Input */}
              <div className="relative flex min-w-0 flex-1 items-center gap-2">
                {imagePreviewUrl ? (
                  <div className="relative h-[36px] w-[36px] shrink-0 md:h-[28px] md:w-[28px]">
                    <img
                      src={imagePreviewUrl}
                      alt={selectedImage?.name || "Selected upload"}
                      className="h-full w-full rounded-[4px] border border-white/20 object-cover"
                    />

                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      aria-label="Remove uploaded image"
                      title="Remove image"
                      className="
                        absolute -right-[6px] -top-[6px]
                        inline-flex h-[17px] w-[17px]
                        items-center justify-center text-center
                        rounded-full border border-white/20
                        bg-[#5A5A5A] pb-1
                        text-[13px] leading-none text-white
                        transition hover:bg-[#747474]
                      "
                    >
                      ×
                    </button>
                  </div>
                ) : null}

                <input
                  type="text"
                  value={value}
                  onChange={handleInputChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();

                      if (!isSubmitDisabled) {
                        handleSubmit();
                      }
                    }
                  }}
                  disabled={isStreaming || isAssistantResponsePending}
                  placeholder={
                    isStreaming || isAssistantResponsePending
                      ? "Please wait for the response..."
                      : "Search for a product, styling advice..."
                  }
                  aria-label="Chat message"
                  className={[
                    `
                h-[46px] min-w-0 w-full flex-1 border-0
                bg-transparent px-0
                font-spaceMono text-[13px] font-normal
                tracking-[0.04em] text-white
                outline-none
                placeholder:text-[#858585]
                placeholder:tracking-[0.04em]

                md:h-[32px] md:text-[12px]
              `,
                    isStreaming || isAssistantResponsePending
                      ? "cursor-not-allowed opacity-60"
                      : "",
                  ].join(" ")}
                />
              </div>
              {!isAuthenticated ? (
                <MobileModeSelect
                  mode={mode}
                  onModeChange={onModeChange}
                />
              ) : null}
              {/* Submit / Start Survey button */}
              <button
                type="button"
                disabled={isBusy}
                onClick={handlePrimaryButtonClick}
                aria-label={primaryButtonLabel}
                title={primaryButtonLabel}
                className={[
                  `
                    inline-flex mt-[4px] md:mt-0 h-[28px] md:h-[34px] shrink-0
                    items-center justify-center
                    rounded-[5px] bg-[#A64346]
                    text-white transition-all
                  `,
                  shouldShowStartSurveyButton
                    ? `
                        w-auto px-4
                        font-spaceMono text-[11px] font-medium
                        tracking-[0.03em]
                      `
                    : "w-[34px]",
                  isBusy
                    ? "cursor-not-allowed opacity-45"
                    : `
                        cursor-pointer
                        active:scale-[0.97]
                        hover:bg-[#A93D46]
                      `,
                ].join(" ")}
              >
                {isFindSearching ? (
                  <span
                    className="
                      h-[18px] w-[18px] animate-spin
                      rounded-full border-2
                      border-white/30 border-t-white
                    "
                  />
                ) : shouldShowStartSurveyButton ? (
                  <span className="whitespace-nowrap">
                    Start Survey
                  </span>
                ) : hasInputValue ? (
                  <DesktopSendIcon />
                ) : isModalOpen ? (
                  <DownArrowIcon className="h-[14px] w-[14px]" />
                ) : (
                  <MobilePopupIcon />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingChatBar;
